import { ACCEPTED_FILE_TYPES, MAX_FILE_SIZE_BYTES } from '@brighte/validation';
import { css } from '@emotion/css';
import { Alert } from '@spark-web/alert';
import { Box } from '@spark-web/box';
import { Dropzone } from '@spark-web/dropzone';
import { Field } from '@spark-web/field';
import { Stack } from '@spark-web/stack';
import { Text } from '@spark-web/text';

type RetireeSuperannuationFormProps = {
  onFileDrop: (event: { target: { value: unknown } }) => void;
  tooManyFilesError: boolean;
  maxFiles: number;
  // Disables the dropzone once a statement is already uploaded (manual review) — the owner must
  // remove it before uploading another.
  disabled?: boolean;
};

// Self-funded retiree document path: upload a superannuation statement.
function RetireeSuperannuationForm({
  onFileDrop,
  tooManyFilesError,
  maxFiles,
  disabled = false,
}: RetireeSuperannuationFormProps) {
  const disabledDropzoneClassName = css({ pointerEvents: 'none', opacity: 0.5 });
  return (
    <Stack gap="xxlarge" marginTop="large">
      {tooManyFilesError && (
        <Alert tone="critical" heading="Too many files selected">
          <Text size="small">
            You can only upload up to {maxFiles} files at once. Please select fewer files and try again.
          </Text>
        </Alert>
      )}
      <Box aria-disabled={disabled} className={disabled ? disabledDropzoneClassName : undefined}>
        <Field label="Upload a copy of your Superannuation statement" disabled={disabled}>
          <Dropzone
            key={disabled ? 'disabled' : 'active'}
            accept={ACCEPTED_FILE_TYPES}
            onChange={disabled ? undefined : onFileDrop}
            maxFileSizeKb={Math.floor(MAX_FILE_SIZE_BYTES / 1024)}
            maxFiles={0}
            multiple={true}
          />
        </Field>
      </Box>
      <Stack gap="large">
        <Text weight="semibold">Not sure which document to provide?</Text>
        <Text size="small">
          To verify your income, please provide a statement from your super fund showing your pension payments (from
          either FY25 or FY26). You can usually download this from your fund&apos;s app or website.
        </Text>
      </Stack>
    </Stack>
  );
}

export default RetireeSuperannuationForm;
